import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap'
import { useAuth } from '../../hooks/useAuth'
import { toast } from 'react-toastify'

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
  orgSlug: z.string().min(1, 'Organization slug is required')
})

type LoginForm = z.infer<typeof loginSchema>

export const LoginPage = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const { login } = useAuth()

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      orgSlug: 'demo',
      email: 'admin@demo.local',
      password: 'Passw0rd!'
    }
  })

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true)
    setError('')

    try {
      await login(data.email, data.password, data.orgSlug)
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Login failed'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="login-container">
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} sm={10} md={8} lg={6} xl={5}>
            <Card className="login-card">
              <Card.Header className="login-header">
                <div className="d-flex align-items-center justify-content-center mb-4">
                  <div className="bg-gradient-primary rounded-circle p-4 me-3 shadow-lg" style={{ width: '80px', height: '80px' }}>
                    <i className="fas fa-headset text-white" style={{ fontSize: '32px' }}></i>
                  </div>
                </div>
                <h2 className="mb-2 fw-bold">AACore Helpdesk</h2>
                <p className="mb-0 fs-5">Welcome back! Please sign in to continue</p>
              </Card.Header>
              
              <Card.Body className="login-body">
                {error && (
                  <Alert variant="danger" className="mb-3" dismissible onClose={() => setError('')}>
                    {error}
                  </Alert>
                )}
                
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <Form.Group className="mb-4">
                    <Form.Label className="d-flex align-items-center">
                      <i className="fas fa-building me-2 text-primary"></i>
                      Organization Slug
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="demo"
                      {...register('orgSlug')}
                      isInvalid={!!errors.orgSlug}
                      className="form-control-lg"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.orgSlug?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="d-flex align-items-center">
                      <i className="fas fa-envelope me-2 text-primary"></i>
                      Email Address
                    </Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="user@demo.local"
                      {...register('email')}
                      isInvalid={!!errors.email}
                      className="form-control-lg"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="d-flex align-items-center">
                      <i className="fas fa-lock me-2 text-primary"></i>
                      Password
                    </Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Enter your password"
                      {...register('password')}
                      isInvalid={!!errors.password}
                      className="form-control-lg"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.password?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Button
                    type="submit"
                    variant="primary"
                    className="w-100 btn-lg"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-2"></i>
                        Signing in...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-sign-in-alt me-2"></i>
                        Sign In
                      </>
                    )}
                  </Button>
                </Form>

              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}
